import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { forwardRef } from '@shared/utils/forwardRef';
import { mergeRefs } from '@shared/utils/hooks/setRef';
import { useIsomorphicLayoutEffect } from '@shared/utils/hooks/useIsomorphicLayoutEffect';
import { Box } from '../Box';
import { useFormControlStyles } from './FormControl.styles';
import { useId } from '@reach/auto-id';
import type { FormControlProps, FormControlOverrides, SharedFormControlProps } from './FormControl.types';
import { FieldErrorAnnouncementContext, FormControlContext } from './FormControl.provider';
import { useOverrides } from '@shared/utils/overrides';
import { announcePolitely } from '@shared/utils/a11y/announcePolitely';

const defaultProps = {
  overrides: {} as FormControlOverrides,
} as const;

export const defaultComponents = {
  Root: Box,
  Label: Box,
  ContentContainer: Box,
  Hint: Box,
} as const;

/** Each mounted field's "read your error out now", inside a FieldErrorAnnouncementProvider. */
const fieldAnnouncers = new WeakMap<Element, () => void>();

/**
 * Reads out the error of the field that contains `element`, for a form whose submit leaves focus in
 * that field: no focus change will read its new description. It goes through the field's own record
 * of what the user has heard, so leaving the field afterwards doesn't read it again, and a second
 * request while the first is still waiting replaces it. Does nothing outside a
 * FieldErrorAnnouncementProvider, or once the field has gone.
 */
export const announceFieldError = (element: Element | null) => {
  for (let node = element; node; node = node.parentElement) {
    const announce = fieldAnnouncers.get(node);
    if (announce) {
      announce();
      return;
    }
  }
};

const FormControl = forwardRef<'div', FormControlProps>((props, ref) => {
  const { caption, children, error, htmlFor, isDisabled, isInvalid, isRequired, label, overrides, onFocus, onBlur, ...restProps } = props as FormControlProps & typeof defaultProps;
  const {
    Root: [Root, rootOverrides],
    Label: [Label, labelOverrides],
    ContentContainer: [ContentContainer, contentOverrides],
    Hint: [Hint, hintOverrides],
  } = useOverrides(defaultComponents, { Root: overrides.Root, Label: overrides.Label, ContentContainer: overrides.ContentContainer, Hint: overrides.Hint });

  const context = useMemo(() => {
    return {
      isDisabled,
      isInvalid,
      isRequired,
    };
  }, [isDisabled, isInvalid, isRequired]);

  const onlyChildProps = React.Children.only(children).props;
  const id = useId(htmlFor || onlyChildProps.id);
  const captionId = `field-${id}-helptext`;
  const sharedProps: SharedFormControlProps = {
    $isInvalid: !!context.isInvalid,
    $isDisabled: !!context.isDisabled,
  };

  const styles = useFormControlStyles(sharedProps);

  // Inside a FieldErrorAnnouncementProvider, an error the user would otherwise miss is read out
  // through a polite live region: one on a field the user has left (validation on blur, or an error
  // that appeared while typing and is still there on leaving), because focus is already elsewhere.
  // Errors that come and go while the user types stay quiet, so typing isn't interrupted. An error
  // focus reads is not announced: focus landing on an element the error describes reads it through
  // `aria-describedby`, and announcing it as well would read it twice.
  const announcesErrors = useContext(FieldErrorAnnouncementContext);
  const hasLeftFieldRef = useRef(false);
  const observedErrorRef = useRef<string | null>(null);
  /** The error the user has heard, announced or read by focus. */
  const heardErrorRef = useRef<string | null>(null);
  const announceTimerRef = useRef<number | undefined>(undefined);
  const withdrawAnnouncementRef = useRef<(() => void) | null>(null);

  const readHint = () => (error ? document.getElementById(captionId)?.textContent?.trim() || null : null);
  /** Whether focusing this element reads the hint: the element itself must name it. */
  const isDescribedByHint = (element: Element | null) => !!element?.getAttribute('aria-describedby')?.split(/\s+/).includes(captionId);
  const cancelAnnouncement = () => {
    window.clearTimeout(announceTimerRef.current);
    withdrawAnnouncementRef.current?.();
    withdrawAnnouncementRef.current = null;
  };
  /** Reads the error out after this tick, unless the user has heard it by then or focus reads it. */
  const scheduleAnnouncement = (text: string) => {
    cancelAnnouncement();
    announceTimerRef.current = window.setTimeout(() => {
      if (heardErrorRef.current === text || isDescribedByHint(document.activeElement)) return;
      heardErrorRef.current = text;
      withdrawAnnouncementRef.current = announcePolitely(text);
    }, 0);
  };

  const rootRef = useRef<HTMLDivElement | null>(null);
  const rootRefs = useMemo(() => mergeRefs<HTMLDivElement>(ref, rootRef), [ref]);
  // Registered on every render, so the request reads this render's error. Layout effects, like the
  // bookkeeping below, so a request made straight after the render (a parent's layout effect) already
  // sees the new error, and the bookkeeping can't cancel it afterwards.
  useIsomorphicLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || !announcesErrors) return undefined;
    fieldAnnouncers.set(root, () => {
      const text = readHint();
      if (!text) return;
      cancelAnnouncement();
      announceTimerRef.current = window.setTimeout(() => {
        heardErrorRef.current = text;
        withdrawAnnouncementRef.current = announcePolitely(text);
      }, 0);
    });
    return () => {
      fieldAnnouncers.delete(root);
    };
  });

  useIsomorphicLayoutEffect(() => {
    if (!announcesErrors) return;
    const text = readHint();
    if (text === observedErrorRef.current) return;
    observedErrorRef.current = text;
    // A cleared or replaced error withdraws whatever is still waiting to be read about the old one.
    cancelAnnouncement();
    if (!text) {
      heardErrorRef.current = null;
      return;
    }
    if (hasLeftFieldRef.current) scheduleAnnouncement(text);
  });
  // Cleanup only: the refs hold the latest timer and announcement.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => cancelAnnouncement(), []);

  const handleFocus: React.FocusEventHandler<HTMLDivElement> = event => {
    hasLeftFieldRef.current = false;
    // Focus that reads the error (the element it lands on names the hint) is a reading, so an
    // announcement still waiting would be a second one. Focus on a part the hint doesn't describe (a
    // composite control's inner input) reads nothing, so the announcement stays.
    if (announcesErrors && isDescribedByHint(event.target)) {
      heardErrorRef.current = readHint();
      cancelAnnouncement();
    }
    onFocus?.(event);
  };
  const handleBlur: React.FocusEventHandler<HTMLDivElement> = event => {
    // Moving between the parts of one control (a phone number's country picker and its number)
    // isn't leaving it.
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      hasLeftFieldRef.current = true;
      // An error that appeared while the user typed, and is still there, is read on the way out.
      const text = announcesErrors ? readHint() : null;
      if (text && text !== heardErrorRef.current) scheduleAnnouncement(text);
    }
    onBlur?.(event);
  };

  // Render

  const renderFormElementContent = (content: Maybe<FormControlProps['error']>) => {
    if (!content) {
      return null;
    }

    return typeof content === 'function' ? content(sharedProps) : content;
  };

  const hasHint = !!(caption || error);

  const renderChildren = () => {
    return React.Children.map(children, (child, index) => {
      if (!child) {
        return;
      }
      const key = child.key || `${index}`;
      // The hint (the error, or the caption when there is no error) describes the field, after
      // whatever the field already names. The error is linked this way only: it is the one relation
      // every screen reader reads, and a second one (`aria-errormessage`) to the same text can make
      // some read it twice.
      const describedBy = [child.props['aria-describedby'], hasHint ? captionId : null].filter(Boolean).join(' ');
      return React.cloneElement(child, {
        key,
        'aria-describedby': describedBy || null,
      });
    });
  };

  const renderHint = () => {
    const hint = error || caption;
    return renderFormElementContent(hint);
  };

  return (
    <FormControlContext.Provider value={context}>
      <Root
        role="group"
        ref={rootRefs}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...rootOverrides}
        {...restProps}
      >
        {label && (
          <Label
            as="label"
            htmlFor={id}
            // State as attributes too, so a themed page can restyle the label at rest without
            // overriding its invalid and disabled colours.
            data-invalid={context.isInvalid || undefined}
            data-disabled={context.isDisabled || undefined}
            {...styles.Label}
            {...labelOverrides}
          >
            {renderFormElementContent(label)}
          </Label>
        )}
        <ContentContainer {...contentOverrides}>{renderChildren()}</ContentContainer>
        {hasHint && (
          <Hint
            id={captionId}
            data-form-hint
            data-invalid={context.isInvalid || undefined}
            {...styles.Hint}
            {...hintOverrides}
          >
            {renderHint()}
          </Hint>
        )}
      </Root>
    </FormControlContext.Provider>
  );
});

FormControl.displayName = 'FormControl';
FormControl.defaultProps = defaultProps;

export { FormControl };
